import React, { useState, useEffect } from "react";
import { Clipboard, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

type ClipboardMediaPasteBoxProps = {
  mediaType: "audio" | "image";
  label?: string;
  title?: string;
  description?: string;
  onFilePaste?: (file: File) => void;
  onUrlPaste?: (url: string) => void;
  acceptedMimeTypes: string[];
  acceptedExtensions: string[];
};

export const ClipboardMediaPasteBox: React.FC<ClipboardMediaPasteBoxProps> = ({
  mediaType,
  label,
  title,
  description,
  onFilePaste,
  onUrlPaste,
  acceptedMimeTypes,
  acceptedExtensions,
}) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Clear messages after 4 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const files = e.clipboardData.files;
    const text = e.clipboardData.getData("text");

    console.log("[CO_REPAIR_CLIPBOARD_PASTE]", {
      mediaType,
      pastedType: files.length > 0 ? "file" : text ? "text" : "unknown",
      hasFile: files.length > 0,
      hasText: !!text,
    });

    // Case A: Files in clipboard
    if (files && files.length > 0) {
      const file = files[0];
      const extension = "." + (file.name.split(".").pop() || "").toLowerCase();

      const mimeValid =
        acceptedMimeTypes.length === 0 ||
        acceptedMimeTypes.some((type) => {
          if (type.endsWith("/*")) {
            return file.type.startsWith(type.slice(0, -2));
          }
          return file.type === type;
        });

      const extValid =
        acceptedExtensions.length === 0 ||
        acceptedExtensions.includes(extension);

      if (mimeValid || extValid) {
        console.log("[CO_REPAIR_CLIPBOARD_FILE_ACCEPTED]", {
          mediaType,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });
        setSuccessMessage(t("clipboardPaste.filePastedSuccess", { name: file.name }));
        setErrorMessage(null);
        if (onFilePaste) onFilePaste(file);
      } else {
        const reason = t("clipboardPaste.invalidFileType", { type: file.type || "desconhecido", expected: acceptedExtensions.join(", ") });
        console.warn("[CO_REPAIR_CLIPBOARD_REJECTED]", {
          mediaType,
          reason,
        });
        setErrorMessage(reason);
        setSuccessMessage(null);
      }
    } 
    // Case B/C: Text in clipboard
    else if (text) {
      const trimmed = text.trim();
      let isUrl = false;
      try {
        new URL(trimmed);
        isUrl = true;
      } catch {
        isUrl = false;
      }

      if (isUrl) {
        const urlWithoutParams = trimmed.split("?")[0].toLowerCase();
        const extValid =
          acceptedExtensions.length === 0 ||
          acceptedExtensions.some((ext) => urlWithoutParams.endsWith(ext) || trimmed.toLowerCase().includes(ext));

        if (extValid) {
          console.log("[CO_REPAIR_CLIPBOARD_URL_ACCEPTED]", {
            mediaType,
            url: trimmed,
          });
          setSuccessMessage(t("clipboardPaste.urlPastedSuccess"));
          setErrorMessage(null);
          if (onUrlPaste) onUrlPaste(trimmed);
        } else {
          const reason = t("clipboardPaste.urlNoExtension", { expected: acceptedExtensions.join(", ") });
          console.warn("[CO_REPAIR_CLIPBOARD_REJECTED]", {
            mediaType,
            reason,
          });
          setErrorMessage(reason);
          setSuccessMessage(null);
        }
      } else {
        const reason = t("clipboardPaste.invalidContent", { type: mediaType === "audio" ? t("common.audio", "áudio") : t("common.image", "imagem") });
        console.warn("[CO_REPAIR_CLIPBOARD_REJECTED]", {
          mediaType,
          reason,
        });
        setErrorMessage(reason);
        setSuccessMessage(null);
      }
    } else {
      const reason = t("clipboardPaste.emptyClipboard");
      console.warn("[CO_REPAIR_CLIPBOARD_REJECTED]", {
        mediaType,
        reason,
      });
      setErrorMessage(reason);
      setSuccessMessage(null);
    }
  };

  const displayTitle = title || label || (mediaType === "audio" ? t("clipboardPaste.audioTitle") : t("clipboardPaste.imageTitle"));
  const defaultDesc = mediaType === "audio"
    ? t("clipboardPaste.audioDesc")
    : t("clipboardPaste.imageDesc");
  const displayDescription = description || defaultDesc;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block">
          {displayTitle}
        </label>
        <span className="text-[9px] font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-md flex items-center gap-1 animate-pulse">
          <Sparkles className="w-2.5 h-2.5" /> {t("clipboardPaste.ctrlVActive")}
        </span>
      </div>

      <div
        tabIndex={0}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 outline-hidden select-none relative flex flex-col items-center justify-center gap-2 ${
          successMessage
            ? "border-green-300 bg-green-50/20"
            : errorMessage
            ? "border-red-300 bg-red-50/20"
            : isFocused
            ? "border-purple-500 bg-purple-50/20 shadow-xs ring-2 ring-purple-500/20"
            : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        {successMessage ? (
          <div className="flex flex-col items-center gap-1 animate-in zoom-in duration-200">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            <p className="text-[11px] font-bold text-green-800 leading-tight">{successMessage}</p>
          </div>
        ) : errorMessage ? (
          <div className="flex flex-col items-center gap-1 animate-in shake duration-200">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <p className="text-[11px] font-bold text-red-800 leading-tight">{errorMessage}</p>
          </div>
        ) : (
          <>
            <Clipboard className={`w-5 h-5 transition-transform duration-200 ${isFocused ? "text-purple-500 scale-110" : "text-gray-400"}`} />
            <div>
              <p className={`text-xs font-bold transition-colors ${isFocused ? "text-purple-950" : "text-gray-700"}`}>
                {displayDescription}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5 leading-normal">
                {isFocused ? t("clipboardPaste.pressCtrlV") : t("clipboardPaste.clickFocusPaste")}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
